import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Eye,
  Scale,
  FileText,
  Mountain,
  Coins,
  Landmark,
  Layers,
  Database,
} from 'lucide-react';
import { colors, gradients } from '../styles/colors';
import { fetchAZSReserveData } from '../services/airtableService';
import { formatNumber, formatCurrency, formatCurrencyFixed, formatPercentage } from '../utils/formatters';
import ContractAddressCard from '../components/ContractAddressCard';
import { BTX_REFERRAL_URL } from '../config/links';

const AZS_CONTRACT_ADDRESS = '0xee7eddf7793c1399407ba0b858852f64830f37a6';

const SectionHeading = ({ eyebrow, title, sub, align = 'center', maxW = 'max-w-3xl' }) => (
  <div className={`${maxW} ${align === 'center' ? 'mx-auto text-center' : ''} mb-12 sm:mb-16`}>
    {eyebrow && (
      <div className={`flex items-center gap-3 mb-4 ${align === 'center' ? 'justify-center' : ''}`}>
        <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
        <span className="text-[11px] uppercase tracking-[0.28em] font-medium" style={{ color: colors.teal }}>{eyebrow}</span>
        {align === 'center' && <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />}
      </div>
    )}
    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight" style={{ color: colors.white }}>{title}</h2>
    {sub && <p className="text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{sub}</p>}
  </div>
);

const AZSReserveDataPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [reserveData, setReserveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animatedValue, setAnimatedValue] = useState(0);
  const navigate = useNavigate();

  const loadReserveData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAZSReserveData();
      setReserveData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const raf = requestAnimationFrame(() => {
      setIsVisible(true);
      loadReserveData();
    });
    return () => cancelAnimationFrame(raf);
  }, [loadReserveData]);

  // Animate silver reserves number
  useEffect(() => {
    if (reserveData && isVisible) {
      const targetValue = reserveData.totalSilverReserves;
      let currentValue = 0;
      const increment = targetValue / 60;
      const timer = setInterval(() => {
        currentValue += increment;
        if (currentValue >= targetValue) {
          currentValue = targetValue;
          clearInterval(timer);
        }
        setAnimatedValue(Math.floor(currentValue));
      }, 16);
      return () => clearInterval(timer);
    }
    // Intentional: mount-only effect; deps intentionally omitted
  }, [reserveData, isVisible]);

  // §4 ecosystem flow
  const flowNodes = [
    { Icon: Mountain, label: 'Mines' },
    { Icon: Coins, label: 'Bullion Providers' },
    { Icon: Database, label: 'Silver Reserve', featured: true },
    { Icon: Landmark, label: 'Liquidity Framework' },
  ];

  // §5 trust pillars
  const pillars = [
    { Icon: ShieldCheck, label: 'Verification' },
    { Icon: FileText, label: 'Reporting' },
    { Icon: Scale, label: 'Governance' },
    { Icon: Eye, label: 'Visibility' },
  ];

  // §6 ecosystem cards
  const ecosystem = [
    { Icon: Landmark, title: 'Liquidity Providers', href: '/liquidity-providers' },
    { Icon: Mountain, title: 'Mines', href: '/mines' },
    { Icon: Coins, title: 'Bullion Providers', href: '/bullion-providers' },
    { Icon: Layers, title: 'Reserve Architecture', href: '/reserve-architecture' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }}>
      {/* §1 HERO */}
      <section className="relative overflow-hidden" style={{ minHeight: '88vh', background: 'linear-gradient(180deg, #000000 0%, #03090C 55%, #07141A 100%)' }}>
        <img
          src="/reserves-hero.webp"
          alt=""
          aria-hidden="true"
          data-testid="azs-hero-image"
          className={`absolute inset-0 w-full h-full object-cover azs-hero-drift transition-opacity duration-[2000ms] ease-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          style={{ objectPosition: 'center center' }}
          loading="eager"
          decoding="async"
        />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.60) 0%, rgba(0,0,0,0.30) 38%, rgba(0,0,0,0.50) 72%, rgba(0,0,0,0.94) 100%)' }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(58% 60% at 50% 50%, rgba(0,128,128,0.10) 0%, rgba(0,0,0,0) 70%)' }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center min-h-[88vh] pt-32 pb-20 sm:pt-36 sm:pb-24">
          <div className={`transition-all duration-1000 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}>
            <div className="flex items-center gap-3 mb-7 justify-center">
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] font-medium" style={{ color: colors.teal, fontFamily: 'inherit' }}>
                <Coins className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: colors.silverMid }} />
                Silver Reserve Data
              </span>
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[64px] leading-[1.05] tracking-[-0.02em] mb-7 max-w-4xl mx-auto" style={{ color: colors.white, fontFamily: 'inherit', fontWeight: 700 }}>
              SILVER RESERVE VISIBILITY.
            </h1>
            <p className="text-base sm:text-lg lg:text-xl leading-[1.55] max-w-2xl mx-auto mb-12" style={{ color: 'rgba(255,255,255,0.72)', fontFamily: 'inherit' }}>
              Explore reserve composition, reserve value, and reserve transparency through the Arizore reserve framework.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => navigate('/reserve-architecture')}
                data-testid="azs-hero-architecture-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{ backgroundColor: colors.teal, color: colors.white, border: `1px solid ${colors.teal}`, fontFamily: 'inherit' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Explore Reserve Architecture
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/reserve-data/azg')}
                data-testid="azs-hero-gold-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)', fontFamily: 'inherit' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
              >
                View Gold Reserve
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes azs-hero-drift { 0% { transform: scale(1.04) translate3d(0,0,0); } 50% { transform: scale(1.06) translate3d(-0.5%, -0.5%, 0); } 100% { transform: scale(1.04) translate3d(0,0,0); } }
          .azs-hero-drift { animation: azs-hero-drift 26s ease-in-out infinite; will-change: transform; }
          @media (prefers-reduced-motion: reduce) { .azs-hero-drift { animation: none; } }
        `}</style>
      </section>

      {/* §2 WHY VISIBILITY MATTERS */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-1.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.1 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.6) 50%, #040404 100%)' }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            eyebrow="Why Visibility Matters"
            title="TRANSPARENCY SUPPORTS CONFIDENCE."
            sub="Reserve visibility forms a core component of the reserve framework. Reporting, verification, and reserve transparency help participants better understand reserve composition and reserve development."
          />
        </div>
      </section>

      {/* §3 LIVE SILVER RESERVE DATA — data block preserved exactly (API, labels, calculations untouched) */}
      <section
        className="relative py-12 sm:py-14 md:py-16 overflow-hidden"
        style={{ backgroundColor: '#050505' }}
      >
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-2.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.07 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.7) 50%, #050505 100%)' }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-bold mb-3 text-center"
            style={{ color: colors.white }}
          >
            AZS Reserve Snapshot
          </h2>
          <p
            className="text-xs sm:text-sm uppercase tracking-[0.22em] text-center mb-5"
            style={{ color: colors.white, opacity: 0.5 }}
          >
            Pledged Assets
          </p>

          {/* Explore AZS on BTX CTA (marketplace link preserved) + RWA.xyz secondary link */}
          <div className="mb-7 sm:mb-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <a
              href={BTX_REFERRAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="azs-explore-on-btx-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300"
              style={{ backgroundColor: 'transparent', color: colors.silverMid, border: `1px solid ${colors.silverMid}`, textDecoration: 'none' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${colors.silverMid}14`; e.currentTarget.style.borderColor = colors.silverStart; e.currentTarget.style.color = colors.silverStart; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = colors.silverMid; e.currentTarget.style.color = colors.silverMid; }}
            >
              Explore AZS on BTX
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <a
              href="https://app.rwa.xyz/assets/AZS"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="azs-view-on-rwa-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-medium transition-all duration-300"
              style={{ backgroundColor: 'transparent', color: colors.white, opacity: 0.85, border: '1px solid rgba(255,255,255,0.25)', textDecoration: 'none' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.opacity = '0.85'; }}
            >
              View AZS on RWA.xyz
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>

          {loading && (
            <div className="text-center py-12">
              <p style={{ color: colors.white, opacity: 0.7 }}>Loading reserve data...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p style={{ color: colors.white, opacity: 0.7 }}>
                Reserve data is temporarily unavailable. Please try again later.
              </p>
              <p className="text-sm mt-2" style={{ color: colors.white, opacity: 0.5 }}>
                Error: {error}
              </p>
            </div>
          )}

          {!loading && !error && reserveData && (
            <div className="space-y-4 sm:space-y-5">
              {/* Top Silver Box - Total Silver Reserves */}
              <div
                className="relative backdrop-blur-sm px-5 py-6 sm:px-8 sm:py-7 md:px-10 md:py-8 transition-all duration-500 transform hover:scale-[1.01] rounded-2xl"
                style={{ 
                  background: gradients.silver,
                  boxShadow: '0 8px 32px rgba(192, 192, 192, 0.3)',
                }}
              >
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div className="flex flex-col md:flex-row items-center md:items-stretch w-full md:w-auto">
                    <div className="text-center md:text-left" data-testid="azs-hero-total-reserves">
                      <span 
                        className="text-xs sm:text-sm font-medium uppercase tracking-[0.14em] block mb-1.5 sm:mb-2"
                        style={{ color: colors.graphite, opacity: 0.75 }}
                      >
                        Total Silver Reserves
                      </span>
                      <div className="flex items-baseline justify-center md:justify-start space-x-3">
                        <span 
                          className="text-[44px] sm:text-5xl md:text-[56px] lg:text-[64px] font-bold tabular-nums leading-none tracking-tight"
                          style={{ color: colors.graphite }}
                        >
                          {formatNumber(animatedValue)}
                        </span>
                        <span 
                          className="text-xl sm:text-2xl md:text-3xl font-semibold"
                          style={{ color: colors.graphite, opacity: 0.65 }}
                        >
                          oz
                        </span>
                      </div>
                    </div>

                    {reserveData.totalReserveValue != null && (
                      <>
                        <div
                          aria-hidden="true"
                          className="hidden md:block w-px self-stretch mx-7 lg:mx-9"
                          style={{ backgroundColor: colors.graphite, opacity: 0.2 }}
                        />
                        <div
                          aria-hidden="true"
                          className="md:hidden h-px w-14 my-4"
                          style={{ backgroundColor: colors.graphite, opacity: 0.2 }}
                        />
                        <div className="text-center md:text-left flex flex-col justify-center" data-testid="azs-hero-total-reserve-value">
                          <span 
                            className="text-xs sm:text-sm font-medium uppercase tracking-[0.14em] block mb-1.5 sm:mb-2"
                            style={{ color: colors.graphite, opacity: 0.75 }}
                          >
                            Total Reserve Value
                          </span>
                          <div className="flex items-baseline justify-center md:justify-start space-x-2">
                            <span 
                              className="text-[26px] sm:text-3xl md:text-[32px] lg:text-[38px] font-bold tabular-nums leading-none tracking-tight whitespace-nowrap"
                              style={{ color: colors.graphite }}
                            >
                              {formatCurrencyFixed(reserveData.totalReserveValue)}
                            </span>
                            <span 
                              className="text-sm sm:text-base md:text-lg font-semibold"
                              style={{ color: colors.graphite, opacity: 0.65 }}
                            >
                              USD
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Silver Token Icon */}
                  <div className="flex-shrink-0 mt-5 md:mt-0 md:ml-6">
                    <img
                      src="/arizore-logo-3d-azs.png"
                      alt=""
                      className="w-[72px] h-[72px] sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 object-contain"
                      style={{
                        filter: 'drop-shadow(0 8px 20px rgba(0, 0, 0, 0.2))',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Two Composition Metric Boxes */}
              <div className="grid grid-cols-2 gap-3 sm:gap-5">
                {/* In-Ground Resources Box */}
                <div
                  data-testid="azs-composition-in-ground"
                  className="backdrop-blur-sm border px-4 py-5 sm:px-6 sm:py-6 rounded-xl transition-all duration-500 hover:shadow-2xl"
                  style={{ 
                    backgroundColor: `${colors.white}08`,
                    borderColor: `${colors.white}15`,
                  }}
                >
                  <h3 
                    className="text-sm sm:text-base font-medium mb-1.5 text-center"
                    style={{ color: colors.white, opacity: 0.7 }}
                  >
                    In-Ground Resources
                  </h3>
                  <div className="text-center">
                    <span 
                      className="text-3xl sm:text-4xl md:text-5xl font-bold tabular-nums leading-none"
                      style={{ color: '#C0C0C0' }}
                    >
                      {formatPercentage(reserveData.compositionIG)}
                    </span>
                    <span 
                      className="text-lg sm:text-2xl font-semibold ml-1.5"
                      style={{ color: '#C0C0C0', opacity: 0.8 }}
                    >
                      %
                    </span>
                  </div>
                </div>

                {/* Bullion Box */}
                <div
                  data-testid="azs-composition-bullion"
                  className="backdrop-blur-sm border px-4 py-5 sm:px-6 sm:py-6 rounded-xl transition-all duration-500 hover:shadow-2xl"
                  style={{ 
                    backgroundColor: `${colors.white}08`,
                    borderColor: `${colors.white}15`,
                  }}
                >
                  <h3 
                    className="text-sm sm:text-base font-medium mb-1.5 text-center"
                    style={{ color: colors.white, opacity: 0.7 }}
                  >
                    Bullion
                  </h3>
                  <div className="text-center">
                    <span 
                      className="text-3xl sm:text-4xl md:text-5xl font-bold tabular-nums leading-none"
                      style={{ color: '#C0C0C0' }}
                    >
                      {formatPercentage(reserveData.compositionBullion)}
                    </span>
                    <span 
                      className="text-lg sm:text-2xl font-semibold ml-1.5"
                      style={{ color: '#C0C0C0', opacity: 0.8 }}
                    >
                      %
                    </span>
                  </div>
                </div>
              </div>

              {/* Token Section */}
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                {/* Total AZS Tokens in Circulation */}
                <div
                  className="backdrop-blur-sm border px-4 py-5 sm:px-6 sm:py-6 rounded-xl transition-all duration-500 hover:shadow-2xl"
                  style={{ 
                    backgroundColor: `${colors.white}08`,
                    borderColor: `${colors.white}15`,
                  }}
                >
                  <h3 
                    className="text-sm sm:text-base font-medium mb-1.5 text-center"
                    style={{ color: colors.white, opacity: 0.7 }}
                  >
                    Total AZS Tokens in Circulation
                  </h3>
                  <div className="text-center">
                    <span 
                      className="text-3xl sm:text-4xl md:text-5xl font-bold tabular-nums leading-none"
                      style={{ color: '#EDEDED' }}
                    >
                      {formatNumber(reserveData.totalTokensInCirculation)}
                    </span>
                  </div>
                </div>

                {/* AZS Current Token Value */}
                <div
                  className="backdrop-blur-sm border px-4 py-5 sm:px-6 sm:py-6 rounded-xl transition-all duration-500 hover:shadow-2xl"
                  style={{ 
                    backgroundColor: `${colors.white}08`,
                    borderColor: `${colors.white}15`,
                  }}
                >
                  <h3 
                    className="text-sm sm:text-base font-medium mb-1.5 text-center"
                    style={{ color: colors.white, opacity: 0.7 }}
                  >
                    AZS Current Token Value
                  </h3>
                  <div className="text-center flex items-baseline justify-center gap-2">
                    <span 
                      className="text-3xl sm:text-4xl md:text-5xl font-bold tabular-nums leading-none"
                      style={{ color: '#EDEDED' }}
                    >
                      {formatCurrency(reserveData.currentTokenValue)}
                    </span>
                    <span 
                      className="text-sm sm:text-base font-medium"
                      style={{ color: colors.white, opacity: 0.7 }}
                    >
                      USD
                    </span>
                  </div>
                </div>
              </div>

              {/* AZS Tokens in Circulation Value (API: azsReserveValue) */}
              <div
                data-testid="azs-tokens-in-circulation-value-card"
                className="backdrop-blur-sm border px-4 py-5 sm:px-6 sm:py-6 rounded-xl transition-all duration-500 hover:shadow-2xl"
                style={{ 
                  backgroundColor: `${colors.white}08`,
                  borderColor: `${colors.white}15`,
                }}
              >
                <h3 
                  className="text-sm sm:text-base font-medium mb-1.5 text-center"
                  style={{ color: colors.white, opacity: 0.7 }}
                >
                  AZS Tokens in Circulation Value
                </h3>
                <div className="text-center flex items-baseline justify-center gap-2">
                  <span 
                    className="text-3xl sm:text-4xl md:text-5xl font-bold tabular-nums leading-none"
                    style={{ color: '#EDEDED' }}
                  >
                    {reserveData.reserveValue != null ? formatCurrency(reserveData.reserveValue) : '—'}
                  </span>
                  {reserveData.reserveValue != null && (
                    <span 
                      className="text-sm sm:text-base font-medium"
                      style={{ color: colors.white, opacity: 0.7 }}
                    >
                      USD
                    </span>
                  )}
                </div>
              </div>

            {/* Contract Address Card */}
            <div>
              <ContractAddressCard 
                address={AZS_CONTRACT_ADDRESS} 
                theme="silver" 
              />
            </div>
          </div>
          )}
        </div>
      </section>

      {/* §4 RESERVE FRAMEWORK CONNECTION */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#040404' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Reserve Framework Connection"
            title="PART OF A BROADER RESERVE ECOSYSTEM."
            sub="The Silver Reserve operates within a broader ecosystem connecting verified assets, reserve visibility, and liquidity pathways."
          />
          <div className="flex flex-col lg:flex-row items-stretch justify-center gap-4 lg:gap-3 mb-12">
            {flowNodes.map(({ Icon, label, featured }, idx) => (
              <React.Fragment key={label}>
                <div
                  data-testid={`azs-flow-${label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex-1 flex flex-col items-center text-center p-6 sm:p-7 rounded-2xl"
                  style={{
                    background: featured
                      ? 'linear-gradient(180deg, rgba(0,128,128,0.12) 0%, rgba(0,128,128,0.03) 100%)'
                      : 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                    border: featured ? `1px solid ${colors.teal}50` : '1px solid rgba(255,255,255,0.08)',
                    boxShadow: featured ? `0 24px 48px -24px ${colors.teal}40` : 'none',
                  }}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}45` }}>
                    <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                  </div>
                  <span className="text-sm font-semibold uppercase tracking-[0.1em] leading-tight" style={{ color: colors.white }}>{label}</span>
                </div>
                {idx < flowNodes.length - 1 && (
                  <div aria-hidden="true" className="flex items-center justify-center">
                    <ArrowRight className="hidden lg:block w-5 h-5" style={{ color: colors.teal, opacity: 0.5 }} strokeWidth={1.5} />
                    <ArrowRight className="lg:hidden w-5 h-5 rotate-90" style={{ color: colors.teal, opacity: 0.5 }} strokeWidth={1.5} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/reserve-architecture')}
              data-testid="azs-framework-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
              style={{ backgroundColor: 'transparent', color: colors.teal, border: `1px solid ${colors.teal}` }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${colors.teal}14`; e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}1f`; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              Explore Reserve Architecture
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* §5 GOVERNANCE & VERIFICATION */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-3.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.09 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.6) 50%, #050505 100%)' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Governance & Verification"
            title="BUILT AROUND TRANSPARENCY."
            sub="The reserve framework is supported through verification standards, reporting processes, governance structures, and reserve visibility."
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {pillars.map(({ Icon, label }) => (
              <div
                key={label}
                data-testid={`azs-pillar-${label.toLowerCase()}`}
                className="flex flex-col items-center text-center p-6 sm:p-8 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}30` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <span className="text-sm font-semibold uppercase tracking-[0.12em] leading-tight" style={{ color: colors.white }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §6 EXPLORE THE ECOSYSTEM */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-1.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.08 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.7) 50%, #040404 100%)' }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Explore The Ecosystem"
            title="ONE CONNECTED RESERVE FRAMEWORK."
            sub="Different participant groups interact with the reserve framework in different ways."
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {ecosystem.map(({ Icon, title, href }) => (
              <button
                key={title}
                onClick={() => navigate(href)}
                data-testid={`azs-ecosystem-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group flex flex-col items-start text-left p-6 sm:p-7 rounded-2xl transition-all duration-500"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}45`; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 22px 44px -22px ${colors.teal}40`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-3 tracking-tight leading-tight" style={{ color: colors.white }}>{title}</h3>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: colors.teal }}>
                  Learn More
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AZSReserveDataPage;
